import React from 'react';
import { 
  Circle, 
  Clock, 
  CheckCircle, 
  Eye, 
  Lock, 
  AlertCircle,
  Loader 
} from 'lucide-react';

/**
 * StatusBadge - Sprint 2 Component
 * 
 * Componente visual para mostrar estados de submissions con colores
 * y iconografía consistente según la guía de diseño EPL.
 * 
 * Estados soportados:
 * - not_started: Gris - No iniciado
 * - in_progress: Azul - En progreso (BUILD phase color)
 * - ready_for_review: Cyan - Listo para revisar (MEASURE phase color)
 * - declared: Verde - Declarado (SIMULATE phase color)
 * - closed: Slate oscuro - Cerrado
 * 
 * Props:
 * - status: string (requerido) - Estado actual del submission
 * - size: 'sm' | 'md' | 'lg' - Tamaño del badge
 * - showIcon: boolean - Mostrar icono
 * - showLabel: boolean - Mostrar etiqueta de texto
 * - className: string - Clases CSS adicionales
 */

const StatusBadge = ({ 
  status, 
  size = 'md', 
  showIcon = true, 
  showLabel = true,
  className = '' 
}) => {
  
  // Configuración de estados según EPL Design System
  const statusConfig = {
    not_started: {
      label: 'No iniciado',
      icon: Circle,
      bgColor: 'bg-slate-100',
      textColor: 'text-slate-600',
      borderColor: 'border-slate-300',
      iconColor: 'text-slate-400'
    },
    in_progress: {
      label: 'En progreso',
      icon: Loader,
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-300',
      iconColor: 'text-blue-500',
      animate: true
    },
    ready_for_review: {
      label: 'Listo para revisar',
      icon: Eye,
      bgColor: 'bg-cyan-100',
      textColor: 'text-cyan-700',
      borderColor: 'border-cyan-300',
      iconColor: 'text-cyan-500'
    },
    declared: {
      label: 'Declarado',
      icon: CheckCircle,
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-300',
      iconColor: 'text-green-500'
    },
    closed: {
      label: 'Cerrado',
      icon: Lock,
      bgColor: 'bg-slate-200',
      textColor: 'text-slate-800',
      borderColor: 'border-slate-400',
      iconColor: 'text-slate-600'
    },
    error: {
      label: 'Error',
      icon: AlertCircle,
      bgColor: 'bg-red-100',
      textColor: 'text-red-700',
      borderColor: 'border-red-300',
      iconColor: 'text-red-500'
    }
  };
  
  const config = statusConfig[status] || statusConfig.error;
  const Icon = config.icon;
  
  // Clases responsivas por tamaño
  const sizeClasses = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      gap: 'gap-1.5'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'w-5 h-5',
      gap: 'gap-2'
    }
  };
  
  const sizes = sizeClasses[size];
  
  return (
    <div 
      className={`
        inline-flex items-center ${sizes.gap} 
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        ${sizes.container}
        border rounded-full font-medium
        transition-all duration-200
        ${className}
      `}
    >
      {showIcon && (
        <Icon 
          className={`
            ${sizes.icon} ${config.iconColor}
            ${config.animate ? 'animate-spin' : ''}
          `} 
        />
      )}
      
      {showLabel && (
        <span className="whitespace-nowrap">
          {config.label}
        </span>
      )}
    </div>
  );
};

export default StatusBadge;
